import {
  Box,
  Divider,
  Heading,
  Icon,
  IconButton,
  InputRightElement,
  Link,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  SimpleGrid,
  Text,
  Image,
  useMediaQuery,
  useDisclosure,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { IconView, IconViewOff, logoTextLight } from 'assets/icons';
import Button from 'components/Button';
import { FormikInput } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import { ControllableModal } from 'components/Modal';
import SlickSlider from 'containers/Auth/Shared/SlickSlider';
import { routePaths } from 'global/routePaths';
import { IADPLogin, useFetchADPloginDetails } from 'hooks/auth/useADPLogin';
import { useLogin } from 'hooks/auth/useLogin';
import { useParamsQuery } from 'hooks/common/useParamsQuery';
import { useUserStore } from 'store/useUserStore';
import toaster from 'utils/toaster';

import { initialLoginFormData, loginFormValidationSchema } from './schema';

const initialADPQuery: IADPLogin = {
  adpLogin: false,
  userId: '',
  app: '',
  account: '',
  users: '',
};

const Login: React.FC<unknown> = () => {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();

  const [enableADPLoginAPI, setEnableADPLoginAPI] = useState<boolean>(false);
  const [adpQuery, setADPQuery] = useState<IADPLogin>(initialADPQuery);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isLargerThan768] = useMediaQuery(['(min-width: 768px)'], { ssr: false });
  const searchQuery = useParamsQuery();

  const { status, mutateAsync } = useLogin(searchQuery);
  const { isInitialLoading } = useFetchADPloginDetails(adpQuery, { enabled: enableADPLoginAPI });
  const setSubscriptionStatus = useUserStore(state => state.setSubscriptionStatus);

  useEffect(() => {
    if (searchQuery.get('adpLogin') === 'true' && !!searchQuery.get('userId')) {
      const query: IADPLogin = {
        adpLogin: true,
        userId: searchQuery.get('userId') || '',
        app: searchQuery.get('app') || '',
        account: searchQuery.get('account') || '',
        users: searchQuery.get('users') || '',
      };
      setADPQuery(query);
      setEnableADPLoginAPI(true);
    }

    if (searchQuery.get('action') === 'pick') {
      toaster.info({ description: `You need to login with ${searchQuery.get('email')} to cherry pick shifts` });
    }

    setSubscriptionStatus('notSet');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickShowPassword = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    showPassword: boolean
  ) => {
    setFieldValue('showPassword', !showPassword);
  };

  const handleFormSubmit = async (values: typeof initialLoginFormData) => {
    const { showPassword, ...postData } = values;
    mutateAsync(postData).catch(err => {
      if (err?.code === 'PASSWORD_EXPIRED') {
        const changePasswordEndpoint = `${routePaths.auth.changePassword}?email=${err?.email || ''}&reset_token=${
          err?.reset_token || ''
        }`;
        navigate(changePasswordEndpoint);
      }

      if (err?.payload?.trial_expired) {
        onOpen();
      }
    });
  };

  if (isInitialLoading) return <LogoLoader />;

  return (
    <SimpleGrid minH={'100vh'} columns={{ base: 1, lg: 2 }} bg="white">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={{
          base: '32px 16px',
          md: '60px',
          lg: '32px',
          xl: '60px',
          '2xl': '100px',
        }}
      >
        <Box width="500px">
          <Link href="https://timeero.com" display="inline-block">
            <Image
              src={logoTextLight}
              alt="Timeero Logo"
              width={{
                base: '200px',
                md: '250px',
              }}
            />
          </Link>
          <Heading as="h2" size="3xl" mt={{ base: '8', md: '14' }} mb="2" textTransform="capitalize">
            {searchQuery.get('from') === 'forgot-password' ? t('auth:loginAgain') : `${t('auth:welcomeBack')} :)`}
          </Heading>
          <Text fontSize="md" fontWeight="semibold" color="gray.500" mb="9">
            {searchQuery.get('from') === 'forgot-password'
              ? t('auth:resetYourPasswordAndSeeWhatsHappening')
              : t('auth:loginToSeeWhatsHappening')}
          </Text>
          <Formik
            initialValues={initialLoginFormData}
            validationSchema={loginFormValidationSchema}
            onSubmit={handleFormSubmit}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <FormikInput type="text" name="email" label={t('auth:emailAddress')} />
                <FormikInput
                  type={values.showPassword ? 'text' : 'password'}
                  name="password"
                  label={t('auth:password')}
                  formControlProps={{ mb: 2 }}
                  inputElement={
                    <InputRightElement width="4.5rem">
                      <IconButton
                        aria-label="toggle view password"
                        size="sm"
                        onClick={() => handleClickShowPassword(setFieldValue, values.showPassword)}
                        icon={values.showPassword ? <Icon as={IconViewOff} /> : <Icon as={IconView} />}
                      ></IconButton>
                    </InputRightElement>
                  }
                />
                <Text fontSize="sm" color="gray.500">
                  <Link as={RouterLink} to={routePaths.auth.forgotPassword} color="primary.200">
                    {t('auth:forgotYourPassword')}
                  </Link>
                </Text>
                <Box>
                  <Button
                    type="submit"
                    colorScheme="secondary"
                    width="100%"
                    size="lg"
                    mt="6"
                    isLoading={status === 'loading'}
                  >
                    {t('auth:logIn')}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
          <Divider colorScheme="gray" borderStyle="dashed" mt="9" mb="6" />
          <Text fontSize="sm" color="gray.500">
            {t('auth:dontHaveATimeeroAccount')}
            <Link as={RouterLink} to={routePaths.auth.signUp} color="primary.200" ml="1">
              {t('auth:signup')}
            </Link>
          </Text>
        </Box>
      </Box>

      <ControllableModal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader>{t('auth:trialExpired')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody mb="6">
            <Text textAlign="center" fontSize="16px">
              {t('auth:companyAccountExpired')} <br /> {t('auth:contactYourAdmin')}
            </Text>
          </ModalBody>
        </ModalContent>
      </ControllableModal>

      {isLargerThan768 && (
        <Box display={{ base: 'none', lg: 'block' }}>
          <SlickSlider />
        </Box>
      )}
    </SimpleGrid>
  );
};

export default Login;
